import React from 'react';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Columns, Filter, HelpCircle, ArrowUpRight, ArrowDownRight, ArrowRight, Users, ShieldAlert, ShieldCheck, AlertTriangle } from 'lucide-react';
const chartData = [{
  month: 'May-25',
  low: 18,
  medium: 95,
  high: 62
}, {
  month: 'Jun-25',
  low: 22,
  medium: 120,
  high: 78
}, {
  month: 'Jul-25',
  low: 25,
  medium: 148,
  high: 98
}, {
  month: 'Aug-25',
  low: 28,
  medium: 175,
  high: 115
}, {
  month: 'Sep-25',
  low: 32,
  medium: 205,
  high: 138
}, {
  month: 'Oct-25',
  low: 35,
  medium: 238,
  high: 155
}, {
  month: 'Nov-25',
  low: 38,
  medium: 265,
  high: 170
}, {
  month: 'Dec-25',
  low: 40,
  medium: 280,
  high: 178
}, {
  month: 'Jan-26',
  low: 41,
  medium: 295,
  high: 185
}, {
  month: 'Feb-26',
  low: 42,
  medium: 305,
  high: 190
}, {
  month: 'Mar-26',
  low: 43,
  medium: 310,
  high: 192
}, {
  month: 'Apr-26',
  low: 42,
  medium: 312,
  high: 178
}];
function CustomTooltip({
  active,
  payload,
  label
}: any) {
  if (!active || !payload || !payload.length) return null;
  const total = payload.reduce((sum: number, entry: any) => sum + entry.value, 0);
  return <div className="bg-white rounded-lg border border-gray-200 shadow-lg p-3 min-w-[160px]" data-id="element-572">
      <p className="text-sm font-semibold text-gray-900 mb-2" data-id="element-573">{label}</p>
      <div className="space-y-1.5" data-id="element-574">
        {payload.map((entry: any, index: number) => <div key={index} className="flex items-center justify-between gap-4" data-id="element-575">
            <div className="flex items-center gap-2" data-id="element-576">
              <div className="w-2.5 h-2.5 rounded-sm" style={{
            backgroundColor: entry.fill
          }} data-id="element-577" />
              <span className="text-xs text-gray-600 capitalize" data-id="element-578">
                {entry.dataKey === 'low' ? 'Low Risk' : entry.dataKey === 'medium' ? 'Medium Risk' : 'High Score'}
              </span>
            </div>
            <span className="text-xs font-semibold text-gray-900" data-id="element-579">
              {entry.value}
            </span>
          </div>)}
        <div className="border-t border-gray-100 pt-1.5 flex items-center justify-between" data-id="element-580">
          <span className="text-xs font-medium text-gray-500" data-id="element-581">Total</span>
          <span className="text-xs font-bold text-gray-900" data-id="element-582">{total}</span>
        </div>
      </div>
    </div>;
}
const accountsData = [{
  id: 1,
  company: 'Dunder Mifflin Paper Company',
  score: 82,
  trend: 5,
  dbt: 4,
  dbtTrend: -2,
  balance: '$12,400',
  balancePct: 25,
  limit: '$50,000',
  assignedTo: 'Dave Davidson',
  added: '3/27/2026',
  status: 'Pending Decision' as const
}, {
  id: 2,
  company: 'Stark Industries',
  score: 91,
  trend: 0,
  dbt: 2,
  dbtTrend: 0,
  balance: '$0',
  balancePct: 0,
  limit: '$250,000',
  assignedTo: 'Sarah Chen',
  added: '1/15/2026',
  status: 'Monitoring' as const
}, {
  id: 3,
  company: 'Wayne Enterprises',
  score: 74,
  trend: -3,
  dbt: 8,
  dbtTrend: 2,
  balance: '$45,000',
  balancePct: 36,
  limit: '$125,000',
  assignedTo: 'Mike Torres',
  added: '12/1/2025',
  status: 'Review Application' as const
}, {
  id: 4,
  company: 'Wonka Industries',
  score: 45,
  trend: -12,
  dbt: 15,
  dbtTrend: 5,
  balance: '$18,200',
  balancePct: 61,
  limit: '$30,000',
  assignedTo: 'Sarah Chen',
  added: '11/10/2025',
  status: 'Watch List' as const
}, {
  id: 5,
  company: 'Acme Corporation',
  score: 38,
  trend: -8,
  dbt: 20,
  dbtTrend: 8,
  balance: '$22,500',
  balancePct: 75,
  limit: '$30,000',
  assignedTo: 'Dave Davidson',
  added: '10/27/2025',
  status: 'Watch List' as const
}, {
  id: 6,
  company: 'Umbrella Corporation',
  score: 28,
  trend: -18,
  dbt: 30,
  dbtTrend: 12,
  balance: '$9,800',
  balancePct: 98,
  limit: '$10,000',
  assignedTo: 'Mike Torres',
  added: '11/1/2025',
  status: 'Review Application' as const
}, {
  id: 7,
  company: 'Initech',
  score: 65,
  trend: 0,
  dbt: 5,
  dbtTrend: 0,
  balance: '$0',
  balancePct: 0,
  limit: '$75,000',
  assignedTo: 'Sarah Chen',
  added: '2/15/2026',
  status: 'Monitoring' as const
}, {
  id: 8,
  company: 'Globex Corporation',
  score: 71,
  trend: 3,
  dbt: 3,
  dbtTrend: -1,
  balance: '$8,100',
  balancePct: 11,
  limit: '$75,000',
  assignedTo: 'Dave Davidson',
  added: '1/20/2026',
  status: 'Monitoring' as const
}];
interface HomePageProps {
  onSelectCompany: () => void;
}
export function HomePage({
  onSelectCompany
}: HomePageProps) {
  const getScoreColor = (score: number) => {
    if (score >= 70) return 'text-green-700 border-green-500';
    if (score >= 40) return 'text-yellow-700 border-yellow-500';
    return 'text-red-700 border-red-500';
  };
  const getTrendIcon = (trend: number) => {
    if (trend > 0) return <ArrowUpRight className="w-3.5 h-3.5 text-green-600" data-id="element-583" />;
    if (trend < 0) return <ArrowDownRight className="w-3.5 h-3.5 text-red-600" data-id="element-584" />;
    return <ArrowRight className="w-3.5 h-3.5 text-gray-400" data-id="element-585" />;
  };
  const getTrendColor = (trend: number) => {
    if (trend > 0) return 'text-green-600';
    if (trend < 0) return 'text-red-600';
    return 'text-gray-500';
  };
  const getDbtTrendColor = (trend: number) => {
    if (trend < 0) return 'text-green-700 border-green-500';
    if (trend > 0) return 'text-red-700 border-red-500';
    return 'text-gray-600 border-gray-400';
  };
  const getBalanceBarColor = (pct: number) => {
    if (pct >= 75) return 'bg-red-400';
    if (pct >= 50) return 'bg-yellow-400';
    return 'bg-indigo-500';
  };
  const totalAccounts = 532;
  const lowRisk = 312;
  const moderateRisk = 178;
  const highRisk = 42;
  return <div className="p-4 max-w-7xl mx-auto space-y-4" data-id="element-586">
      {/* Metrics + Chart Row */}
      <div className="flex gap-4" data-id="element-587">
        {/* Metric Cards */}
        <div className="grid grid-cols-2 gap-3 shrink-0 w-72" data-id="element-588">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 flex flex-col items-center justify-center" data-id="element-589">
            <Users className="w-7 h-7 text-indigo-600 mb-1.5" data-id="element-590" />
            <span className="text-3xl font-bold text-gray-900" data-id="element-591">
              {totalAccounts}
            </span>
            <span className="text-xs text-gray-500 font-medium text-center leading-tight mt-0.5" data-id="element-592">
              Total Accounts
            </span>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 flex flex-col items-center justify-center" data-id="element-593">
            <ShieldCheck className="w-7 h-7 text-green-600 mb-1.5" data-id="element-594" />
            <span className="text-3xl font-bold text-green-700" data-id="element-595">{lowRisk}</span>
            <span className="text-xs text-gray-500 font-medium text-center leading-tight mt-0.5" data-id="element-596">
              Low Risk
            </span>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 flex flex-col items-center justify-center" data-id="element-597">
            <AlertTriangle className="w-7 h-7 text-yellow-600 mb-1.5" data-id="element-598" />
            <span className="text-3xl font-bold text-yellow-700" data-id="element-599">
              {moderateRisk}
            </span>
            <span className="text-xs text-gray-500 font-medium text-center leading-tight mt-0.5" data-id="element-600">
              Moderate Risk
            </span>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 flex flex-col items-center justify-center" data-id="element-601">
            <ShieldAlert className="w-7 h-7 text-red-600 mb-1.5" data-id="element-602" />
            <span className="text-3xl font-bold text-red-700" data-id="element-603">{highRisk}</span>
            <span className="text-xs text-gray-500 font-medium text-center leading-tight mt-0.5" data-id="element-604">
              High Risk
            </span>
          </div>
        </div>

        {/* Chart */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 flex-1 min-w-0" data-id="element-605">
          <div className="flex items-center justify-between mb-4" data-id="element-606">
            <h2 className="text-base font-bold text-gray-900" data-id="element-607">
              Historical Pulse Score Summary
            </h2>
            <div className="flex items-center gap-4" data-id="element-608">
              <div className="flex items-center gap-1.5" data-id="element-609">
                <div className="w-3 h-2.5 rounded-sm bg-[#f87171]" data-id="element-610" />
                <span className="text-xs text-gray-600" data-id="element-611">Low</span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-612">
                <div className="w-3 h-2.5 rounded-sm bg-[#fbbf24]" data-id="element-613" />
                <span className="text-xs text-gray-600" data-id="element-614">Medium</span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-615">
                <div className="w-3 h-2.5 rounded-sm bg-[#86efac]" data-id="element-616" />
                <span className="text-xs text-gray-600" data-id="element-617">High</span>
              </div>
            </div>
          </div>
          <div className="h-52 w-full" data-id="element-618">
            <ResponsiveContainer width="100%" height="100%" data-id="element-619">
              <BarChart data={chartData} margin={{
              top: 0,
              right: 0,
              left: -20,
              bottom: 0
            }} data-id="element-620">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" data-id="element-621" />
                <XAxis dataKey="month" axisLine={true} tickLine={true} tick={{
                fill: '#6b7280',
                fontSize: 11
              }} dy={8} data-id="element-622" />
                <YAxis axisLine={true} tickLine={true} tick={{
                fill: '#6b7280',
                fontSize: 11
              }} domain={[0, 600]} ticks={[0, 100, 200, 300, 400, 500, 600]} data-id="element-623" />
                <Tooltip content={<CustomTooltip data-id="element-625" />} cursor={{
                fill: 'rgba(99, 102, 241, 0.06)'
              }} data-id="element-624" />
                <Bar dataKey="low" stackId="a" fill="#f87171" radius={[0, 0, 0, 0]} data-id="element-626" />
                <Bar dataKey="medium" stackId="a" fill="#fbbf24" data-id="element-627" />
                <Bar dataKey="high" stackId="a" fill="#86efac" radius={[3, 3, 0, 0]} data-id="element-628" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-629">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between bg-gray-50/50" data-id="element-630">
          <h2 className="text-base font-bold text-gray-900" data-id="element-631">
            Monitored Accounts
          </h2>
          <div className="flex items-center gap-3" data-id="element-632">
            <button className="flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-700 px-2 py-1 rounded hover:bg-indigo-50 transition-colors" data-id="element-633">
              <Columns className="w-3.5 h-3.5" data-id="element-634" />
              COLUMNS
            </button>
            <button className="flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-700 px-2 py-1 rounded hover:bg-indigo-50 transition-colors" data-id="element-635">
              <Filter className="w-3.5 h-3.5" data-id="element-636" />
              FILTERS
            </button>
          </div>
        </div>

        <div className="overflow-x-auto" data-id="element-637">
          <table className="w-full text-left border-collapse" data-id="element-638">
            <thead data-id="element-639">
              <tr className="border-b border-gray-200 bg-gray-50/50" data-id="element-640">
                <th className="py-2 px-4 text-xs font-medium text-gray-600" data-id="element-641">
                  Company
                </th>
                <th className="py-2 px-4 text-xs font-medium text-gray-600" data-id="element-642">
                  Pulse Score
                </th>
                <th className="py-2 px-4 text-xs font-medium text-gray-600" data-id="element-643">
                  1M Trend ↑
                </th>
                <th className="py-2 px-4 text-xs font-medium text-gray-600" data-id="element-644">
                  DBT
                </th>
                <th className="py-2 px-4 text-xs font-medium text-gray-600" data-id="element-645">
                  Balance / Limit
                </th>
                <th className="py-2 px-4 text-xs font-medium text-gray-600" data-id="element-646">
                  Assigned To
                </th>
                <th className="py-2 px-4 text-xs font-medium text-gray-600" data-id="element-647">
                  Added
                </th>
                <th className="py-2 px-4 text-xs font-medium text-gray-600 text-right" data-id="element-648">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100" data-id="element-649">
              {accountsData.map((account, index) => <tr key={account.id} className={`group transition-colors ${index === 0 ? 'hover:bg-gray-50 cursor-pointer' : ''}`} onClick={() => index === 0 && onSelectCompany()} data-id="element-650">
                  <td className="py-2.5 px-4" data-id="element-651">
                    <span className={`text-sm ${index === 0 ? 'text-indigo-600 font-medium group-hover:underline' : 'text-gray-900'}`} data-id="element-652">
                      {account.company}
                    </span>
                  </td>
                  <td className="py-2.5 px-4" data-id="element-653">
                    <div className={`inline-flex items-center justify-center w-9 h-6 rounded-full border ${getScoreColor(account.score)} bg-white text-xs font-semibold`} data-id="element-654">
                      {account.score}
                    </div>
                  </td>
                  <td className="py-2.5 px-4" data-id="element-655">
                    <div className="flex items-center gap-1" data-id="element-656">
                      {getTrendIcon(account.trend)}
                      <span className={`text-xs font-medium ${getTrendColor(account.trend)}`} data-id="element-657">
                        {Math.abs(account.trend)}
                      </span>
                    </div>
                  </td>
                  <td className="py-2.5 px-4" data-id="element-658">
                    <div className="flex items-center gap-2" data-id="element-659">
                      <span className="text-xs text-gray-900" data-id="element-660">
                        {account.dbt}
                      </span>
                      <div className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded-full border ${getDbtTrendColor(account.dbtTrend)} bg-white`} data-id="element-661">
                        {account.dbtTrend < 0 ? <ArrowDownRight className="w-2.5 h-2.5" data-id="element-662" /> : account.dbtTrend > 0 ? <ArrowUpRight className="w-2.5 h-2.5" data-id="element-663" /> : <ArrowRight className="w-2.5 h-2.5" data-id="element-664" />}
                        <span className="text-[10px] font-medium" data-id="element-665">
                          {Math.abs(account.dbtTrend)}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="py-2.5 px-4" data-id="element-666">
                    <div className="flex flex-col gap-0.5" data-id="element-667">
                      <div className="flex items-center gap-1" data-id="element-668">
                        <span className="text-xs text-gray-900" data-id="element-669">
                          {account.balance}
                        </span>
                        <span className="text-[10px] text-gray-500" data-id="element-670">
                          {account.balancePct}%
                        </span>
                      </div>
                      <div className="w-20 h-1 bg-gray-200 rounded-full overflow-hidden" data-id="element-671">
                        <div className={`h-full ${getBalanceBarColor(account.balancePct)} rounded-full`} style={{
                      width: `${account.balancePct}%`
                    }} data-id="element-672" />
                      </div>
                      <span className="text-[10px] text-gray-500" data-id="element-673">
                        {account.limit}
                      </span>
                    </div>
                  </td>
                  <td className="py-2.5 px-4" data-id="element-674">
                    <span className="text-xs text-gray-700" data-id="element-675">
                      {account.assignedTo}
                    </span>
                  </td>
                  <td className="py-2.5 px-4" data-id="element-676">
                    <span className="text-xs text-gray-700" data-id="element-677">
                      {account.added}
                    </span>
                  </td>
                  <td className="py-2.5 px-4 text-right" data-id="element-678">
                    {account.status === 'Pending Decision' ? <motion.span animate={{
                  boxShadow: ['0 0 0 0 rgba(79, 70, 229, 0.3)', '0 0 0 6px rgba(79, 70, 229, 0)', '0 0 0 0 rgba(79, 70, 229, 0)']
                }} transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} className="inline-block px-2.5 py-1 text-[11px] font-semibold text-white bg-indigo-600 rounded-full" data-id="element-679">
                        {account.status}
                      </motion.span> : <span className={`inline-block px-2.5 py-1 text-[11px] font-medium rounded-full ${account.status === 'Monitoring' ? 'text-green-700 bg-green-50 border border-green-200' : account.status === 'Watch List' ? 'text-red-700 bg-red-50 border border-red-200' : 'text-yellow-700 bg-yellow-50 border border-yellow-200'}`} data-id="element-680">
                        {account.status}
                      </span>}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}